import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, IconButton, MenuItem, Stack, TextField, Tooltip } from '@mui/material';
import { DataGrid } from '../../components/common/datagrid/ResponsiveDataGrid';
import ReplayIcon from '@mui/icons-material/Replay';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import * as approvalV2Api from '../../lib/api/approvalV2';
import * as hrAttendApi from '../../lib/api/hrAttend';
import * as projectApi from '../../lib/api/project';

const DOC_TYPE_LABELS = {
  HR_LEAVE: '휴가 신청',
  ORDER_BOOK: '수주품의서',
};

const DOC_STATUS_LABELS = {
  IN_PROGRESS: '진행중',
  APPROVED: '승인',
  REJECTED: '반려',
  RECALLED: '회수',
  CANCELED: '취소',
};

const DOC_STATUS_COLORS = {
  IN_PROGRESS: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
  RECALLED: 'default',
  CANCELED: 'default',
};

const ApprovalSubmittedV2Container = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [docTypeFilter, setDocTypeFilter] = useState('');
  const [keyword, setKeyword] = useState('');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await approvalV2Api.getApprovalV2Submitted();
        setList(res.data.list || []);
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.message || '내 기안함 조회에 실패했습니다.', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [enqueueSnackbar, tick]);

  const handleRecall = async (row) => {
    try {
      if (row.docType === 'HR_LEAVE') {
        await hrAttendApi.recallLeaveRequest(row.refDocId);
      } else if (row.docType === 'ORDER_BOOK') {
        await projectApi.recallReceiveOrderApprovalV2(row.refDocId);
      } else {
        throw new Error('지원하지 않는 문서 유형입니다.');
      }

      enqueueSnackbar('기안 문서를 회수했습니다.', { variant: 'success' });
      setTick((value) => value + 1);
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.message || e.message || '회수에 실패했습니다.', { variant: 'error' });
    }
  };

  const filteredList = useMemo(() => {
    return (list || []).filter((row) => {
      const matchesDocType = !docTypeFilter || row.docType === docTypeFilter;
      const matchesKeyword = !keyword.trim()
        || [row.refDocNo, row.docTitle, row.submittedByNm, row.currentActorNm]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(keyword.trim().toLowerCase()));
      return matchesDocType && matchesKeyword;
    });
  }, [docTypeFilter, keyword, list]);

  const columns = [
    {
      field: 'docType',
      headerName: '문서유형',
      minWidth: 120,
      flex: 0.6,
      renderCell: ({ value }) => (
        <Chip size="small" label={DOC_TYPE_LABELS[value] || value} variant="outlined" />
      ),
    },
    { field: 'refDocNo', headerName: '문서번호', minWidth: 170, flex: 0.8 },
    { field: 'docTitle', headerName: '문서명', minWidth: 300, flex: 1.5 },
    {
      field: 'currentActorNm',
      headerName: '현재 결재자',
      minWidth: 120,
      flex: 0.6,
      valueFormatter: (value) => value || '-',
    },
    {
      field: 'currentStepOrder',
      headerName: '단계',
      minWidth: 90,
      flex: 0.4,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => `${row.currentStepOrder || 1} / ${row.stepCount || 1}`,
    },
    {
      field: 'docStatus',
      headerName: '상태',
      minWidth: 110,
      flex: 0.5,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ value }) => (
        <Chip
          size="small"
          label={DOC_STATUS_LABELS[value] || value}
          color={DOC_STATUS_COLORS[value] || 'default'}
        />
      ),
    },
    {
      field: 'submittedAt',
      headerName: '상신일',
      minWidth: 160,
      flex: 0.8,
      valueFormatter: (value) => value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: 90,
      sortable: false,
      renderCell: ({ row }) => (
        row.docStatus === 'IN_PROGRESS' ? (
          <Tooltip title="회수">
            <IconButton
              size="small"
              color="warning"
              onClick={(event) => {
                event.stopPropagation();
                handleRecall(row);
              }}
            >
              <ReplayIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null
      ),
    },
  ];

  return (
    <>
      <BreadcrumbsContainer title="결재관리/내 기안함" />
      <Alert severity="info" sx={{ mb: 2 }}>
        approval v2 문서 기준으로 내 기안함을 조회합니다. 새 문서 상신은 휴가 신청, 수주품의서 등 각 업무 화면에서 진행합니다.
      </Alert>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="문서유형"
          value={docTypeFilter}
          onChange={(event) => setDocTypeFilter(event.target.value)}
          sx={{ width: 160 }}
        >
          <MenuItem value="">전체</MenuItem>
          <MenuItem value="HR_LEAVE">휴가 신청</MenuItem>
          <MenuItem value="ORDER_BOOK">수주품의서</MenuItem>
        </TextField>
        <TextField
          size="small"
          label="검색어"
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          sx={{ width: 320 }}
        />
        <Box sx={{ flex: 1 }} />
      </Stack>

      <DataGrid
        rows={filteredList}
        columns={columns}
        getRowId={(row) => row.apprDocId}
        loading={loading}
        autoHeight
        density="compact"
        disableRowSelectionOnClick
        onRowClick={(params) => navigate(`/approvalmng/${params.row.apprDocId}`, {
          state: { from: 'submitted' },
        })}
        sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
      />
    </>
  );
};

export default ApprovalSubmittedV2Container;
